/* src/modules/performance/TeamPerformance.js */
import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, updateDoc, doc, orderBy, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
    CheckCircleIcon, 
    EyeIcon, 
    UserGroupIcon,
    SendIcon,
    SaveIcon,
    XCircleIcon,
    CheckBadgeIcon,
    LockClosedIcon,
    DownloadIcon 
} from '../../components/icons/Icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// --- CONSTANTS ---
const RATING_OPTIONS = [
    "Greatly Exceeds Expectations",
    "Exceeds Expectations",
    "Meets Expectations",
    "Needs Improvement",
    "Needs Significant Improvement"
];

const formatDateISO = (val) => {
    if (!val) return '-';
    try {
        const date = val.toDate ? val.toDate() : new Date(val);
        return date.toISOString().split('T')[0];
    } catch (e) {
        return val;
    }
};

const formatDateTime = (val) => {
    if (!val) return '-';
    try {
        const date = val.toDate ? val.toDate() : new Date(val);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (e) {
        return val;
    }
};

export default function TeamPerformance({ userProfile, appId }) {
    const [view, setView] = useState('list');
    const [teamReviews, setTeamReviews] = useState([]);
    const [filteredReviews, setFilteredReviews] = useState([]);
    const [selectedReview, setSelectedReview] = useState(null);
    const [reviewTemplate, setReviewTemplate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    
    const [selectedCycle, setSelectedCycle] = useState('All');
    const [availableCycles, setAvailableCycles] = useState([]);

    useEffect(() => {
        // --- STRICT MANAGER LOGIC ---
        // Removed Admin check. Only fetch where THIS user is the manager.
        const reviewsRef = collection(db, `artifacts/${appId}/performance_reviews`);
        const q = query(
            reviewsRef, 
            where('managerId', '==', userProfile.uid), 
            orderBy('updatedAt', 'desc')
        );

        const unsub = onSnapshot(q, (snapshot) => {
            const reviews = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                // Double safety: Ensure I don't see myself if I am my own manager
                .filter(r => r.userId !== userProfile.uid);

            setTeamReviews(reviews);
            const cycles = [...new Set(reviews.map(r => r.cycle))];
            setAvailableCycles(cycles);
            setIsLoading(false);
        });

        return () => unsub();
    }, [appId, userProfile]);

    useEffect(() => {
        if (selectedCycle === 'All') {
            setFilteredReviews(teamReviews);
        } else {
            setFilteredReviews(teamReviews.filter(r => r.cycle === selectedCycle));
        }
    }, [selectedCycle, teamReviews]);

    const handleOpenReview = async (review) => {
        setIsLoading(true);
        try {
            const tDoc = await getDoc(doc(db, `artifacts/${appId}/performance_templates`, review.templateId));
            if (tDoc.exists()) {
                setReviewTemplate({ id: tDoc.id, ...tDoc.data() });
                setSelectedReview(review);
                setView('review');
            } else {
                alert("Error: Template for this review not found.");
            }
        } catch (error) {
            console.error(error);
            alert("Error loading review details.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmitToHR = async (review) => {
        if (!window.confirm(`Submit ${review.userName}'s review to HR? This will lock the review permanently. Please ensure the skip level manager has checked the review and you have completed your discussion with ${review.userName}. Proceed?`)) return;
        try {
            await updateDoc(doc(db, `artifacts/${appId}/performance_reviews`, review.id), {
                status: 'Completed',
                hrSubmittedAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });
        } catch (error) {
            console.error(error);
            alert("Failed to submit to HR.");
        }
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div>
            {view === 'list' && (
                <div className="space-y-6">
                    {/* Header & Filter */}
                    <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <div className="flex items-center space-x-3 mb-4 md:mb-0">
                            <UserGroupIcon className="h-8 w-8 text-[#20BEC6]" />
                            <div>
                                <h2 className="text-xl font-bold text-[#213368]">Team Reviews</h2>
                                <p className="text-sm text-gray-500">
                                    Performance reviews for your direct reports.
                                </p>
                            </div>
                        </div>
                        {availableCycles.length > 0 && (
                            <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-600">Filter Cycle:</span>
                                <select 
                                    value={selectedCycle} 
                                    onChange={(e) => setSelectedCycle(e.target.value)}
                                    className="p-2 border rounded-lg bg-gray-50 text-sm focus:ring-2 focus:ring-[#20BEC6]"
                                >
                                    <option value="All">All Cycles</option>
                                    {availableCycles.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                        {/* --- NEW: Scrollable Wrapper --- */}
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {/* --- NEW: Added whitespace-nowrap to headers to keep them on one line --- */}
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Employee</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Cycle</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Submitted On</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Last Action</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Assess</th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Submit to HR</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                {filteredReviews.map(r => (
                                    <tr key={r.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap font-medium text-[#213368]">{r.userName}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{r.cycle}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <StatusBadge status={r.status} />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                            {formatDateTime(r.submittedAt)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                            {formatDateTime(r.updatedAt)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button 
                                                onClick={() => handleOpenReview(r)} 
                                                className="text-blue-600 hover:text-blue-800 flex items-center text-sm font-medium disabled:opacity-50"
                                            >
                                                <EyeIcon className="h-4 w-4 mr-1" /> 
                                                {r.status === 'Completed' || r.status === 'Review Closed' ? 'View' : r.status === 'Draft' ? 'View Progress' : 'Assess'}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            {r.status === 'Completed' || r.status === 'Review Closed' ? (
                                                <LockClosedIcon className="h-5 w-5 text-gray-400 mx-auto" title="Locked" />
                                            ) : (
                                                <button 
                                                    onClick={() => handleSubmitToHR(r)}
                                                    disabled={r.status !== 'Review Complete'} 
                                                    className={`p-2 rounded-full mx-auto transition ${
                                                        r.status === 'Review Complete' 
                                                            ? 'text-[#20BEC6] hover:bg-blue-50 cursor-pointer' 
                                                            : 'text-gray-300 cursor-not-allowed'
                                                    }`}
                                                    title={r.status === 'Review Complete' ? "Submit to HR (Lock)" : "Complete assessment first"}
                                                >
                                                    <SendIcon className="h-5 w-5" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {filteredReviews.length === 0 && (
                                    <tr><td colSpan="7" className="text-center py-12 text-gray-500">
                                        <p className="font-medium">No reviews assigned to you.</p>
                                        <p className="text-sm mt-1">You will only see reviews for your direct reports here.</p>
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
		       </div> 
                    </div>
                </div>
            )}

            {view === 'review' && selectedReview && reviewTemplate && (
                <ManagerAssessmentForm 
                    appId={appId}
                    review={selectedReview}
                    template={reviewTemplate}
                    managerName={userProfile.name}
                    onBack={() => setView('list')}
                />
            )}
        </div>
    );
}

// ... (Rest of Helper Functions & Components: StatusBadge, ManagerAssessmentForm, ReadOnlyTable remain unchanged) ...
// (Include the rest of the file content from the previous TeamPerformance step here to ensure it's complete)
function StatusBadge({ status }) {
    const styles = {
        'Draft': 'bg-yellow-100 text-yellow-800',
        'Submitted': 'bg-blue-100 text-blue-800',
        'Manager-Review': 'bg-purple-100 text-purple-800',
        'Review Complete': 'bg-teal-100 text-teal-800',
        'Completed': 'bg-green-100 text-green-800',
        'Review Closed': 'bg-gray-200 text-gray-700'
    };
    return (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[status] || 'bg-gray-100'}`}>
            {status}
        </span>
    );
}

function ManagerAssessmentForm({ appId, review, template, managerName, onBack }) {
    const [managerComments, setManagerComments] = useState(review.managerComments || {});
    const [finalRating, setFinalRating] = useState(review.finalRating || '');
    const [isSaving, setIsSaving] = useState(false);

    const isEditable = review.status === 'Submitted' || review.status === 'Manager-Review' || review.status === 'Review Complete';

    const handleCommentChange = (sectionId, value) => {
        setManagerComments(prev => ({ ...prev, [sectionId]: value }));
    };

    const handleSaveProgress = async () => {
        setIsSaving(true);
        try {
            await updateDoc(doc(db, `artifacts/${appId}/performance_reviews`, review.id), {
                managerComments,
                finalRating,
                status: 'Manager-Review', 
                updatedAt: Timestamp.now()
            });
            alert("Progress saved.");
        } catch (error) {
            console.error(error);
            alert("Save failed.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSendBack = async () => {
        const reason = prompt("Reason for sending back (e.g., 'Please elaborate on Section 2'):");
        if (!reason) return;

        setIsSaving(true);
        try {
            await updateDoc(doc(db, `artifacts/${appId}/performance_reviews`, review.id), {
                status: 'Draft',
                managerComments,
                rejectionReason: reason,
                updatedAt: Timestamp.now()
            });
            onBack();
        } catch (error) {
            console.error(error);
            alert("Failed to send back.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleCompleteReview = async () => {
        const ratingSection = template.sections.find(s => s.type === 'rating');
        const overallComment = ratingSection ? managerComments[ratingSection.id] : '';

        if (!finalRating || !overallComment || !overallComment.trim()) {
            return alert("You must provide both a 'Final Rating' AND 'Overall Summary Comments' to complete the review.");
        }

        if (!window.confirm("Mark review as complete? This indicates you are ready to discuss with the employee.")) return;

        setIsSaving(true);
        try {
            await updateDoc(doc(db, `artifacts/${appId}/performance_reviews`, review.id), {
                managerComments,
                finalRating,
                managerName,
                status: 'Review Complete',
                updatedAt: Timestamp.now()
            });
            onBack();
        } catch (error) {
            console.error(error);
            alert("Error completing review.");
        } finally {
            setIsSaving(false);
        }
    };

    // --- UPDATED PDF FUNCTION FOR MANAGER VIEW ---
    const handleDownloadPDF = () => {
        const doc = new jsPDF();
        
        const logo = new Image();
        logo.src = '/Brand_logo.png';
        
        logo.onload = () => {
            const imgProps = doc.getImageProperties(logo);
            const pdfLogoWidth = 40; 
            const pdfLogoHeight = (imgProps.height * pdfLogoWidth) / imgProps.width;

            const darkBlue = [33, 51, 104];
            const teal = [32, 190, 198];

            // Header Images & Title
            doc.addImage(logo, 'PNG', 14, 10, pdfLogoWidth, pdfLogoHeight);
            
            doc.setFontSize(10);
            doc.setTextColor(200, 0, 0);
            doc.text("CONFIDENTIAL", 195, 15, { align: "right" });
            
            doc.setTextColor(...darkBlue);
            doc.setFontSize(16);
            doc.text("Performance Review", 14, pdfLogoHeight + 18);
            
            // --- METADATA SECTION (UPDATED) ---
            doc.setTextColor(0);
            doc.setFontSize(10);
            const startY = pdfLogoHeight + 25;
            
            doc.text(`Employee: ${review.userName}`, 14, startY);
            doc.text(`Manager: ${managerName}`, 14, startY + 5);
            doc.text(`Cycle: ${review.cycle}`, 14, startY + 10);
            
            // [NEW] Added Status Line
            doc.text(`Status: ${review.status}`, 14, startY + 15); 
            
            // Shifted Date down by 5 units
            doc.text(`Date: ${formatDateISO(new Date())}`, 14, startY + 20);
            
            // Shifted Divider Line down by 5 units
            doc.setDrawColor(...teal);
            doc.setLineWidth(0.5);
            doc.line(14, startY + 25, 196, startY + 25);

            // Shifted Content Start position down
            let finalY = startY + 33; 
            // ----------------------------------

            template.sections.forEach((section, index) => {
                if (finalY > 250) { doc.addPage(); finalY = 20; }

                doc.setTextColor(...darkBlue);
                doc.setFontSize(12);
                doc.setFont("helvetica", "bold");
                doc.text(`${index + 1}. ${section.title}`, 14, finalY);
                finalY += 8;

                doc.setTextColor(0);
                doc.setFontSize(10);
                doc.setFont("helvetica", "normal");

                const employeeResponse = review.employeeResponses?.[section.id] || [];
                const mgrComment = managerComments[section.id] || "No comments.";

                if (section.type === 'text') {
                    const text = typeof employeeResponse === 'string' ? employeeResponse : "No response.";
                    const splitText = doc.splitTextToSize(text, 180);
                    doc.text(splitText, 14, finalY);
                    finalY += (splitText.length * 5) + 5;
                } 
                else if (section.type === 'achievements_table') {
                    const bodyData = Array.isArray(employeeResponse) ? employeeResponse.map(row => [row.text, row.value]) : [];
                    autoTable(doc, {
                        startY: finalY,
                        head: [['Accomplishment', 'Value']],
                        body: bodyData,
                        theme: 'grid',
                        headStyles: { fillColor: darkBlue },
                        margin: { left: 14, right: 14 }
                    });
                    finalY = doc.lastAutoTable.finalY + 5;
                }
                else if (section.type === 'standard_table') {
                    const bodyData = Array.isArray(employeeResponse) ? employeeResponse.map(row => [row.text]) : [];
                    autoTable(doc, {
                        startY: finalY,
                        head: [['Description']],
                        body: bodyData,
                        theme: 'grid',
                        headStyles: { fillColor: darkBlue },
                        margin: { left: 14, right: 14 }
                    });
                    finalY = doc.lastAutoTable.finalY + 5;
                }
                else if (section.type === 'training_table') {
                    const bodyData = Array.isArray(employeeResponse) ? employeeResponse.map(row => [row.category, row.name, row.status]) : [];
                    autoTable(doc, {
                        startY: finalY,
                        head: [['Category', 'Activity', 'Status']],
                        body: bodyData,
                        theme: 'grid',
                        headStyles: { fillColor: darkBlue },
                        margin: { left: 14, right: 14 }
                    });
                    finalY = doc.lastAutoTable.finalY + 5;
                }

                if (finalY > 250) { doc.addPage(); finalY = 20; }
                
                doc.setFont("helvetica", "bold");
                doc.setTextColor(100);
                doc.text("Manager Feedback:", 14, finalY);
                finalY += 5;
                
                doc.setFont("helvetica", "normal");
                doc.setTextColor(0);
                
                if (section.type === 'rating') {
                    doc.text(`Final Rating: ${finalRating}`, 14, finalY);
                    finalY += 6;
                }

                const splitComment = doc.splitTextToSize(mgrComment, 180);
                doc.text(splitComment, 14, finalY);
                finalY += (splitComment.length * 5) + 15;
            });

            const safeName = review.userName.replace(/[^a-z0-9]/gi, '_');
            const safeCycle = review.cycle.replace(/[^a-z0-9]/gi, '_');
            doc.save(`${safeCycle}_${safeName}.pdf`);
        };
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <button onClick={onBack} className="text-sm text-gray-500 hover:text-[#213368] flex items-center">← Back to Team Reviews</button>
                <button onClick={handleDownloadPDF} className="flex items-center text-sm text-[#20BEC6] hover:text-[#1a9ca3] font-medium"><DownloadIcon className="h-5 w-5 mr-1" /> Download PDF</button>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h2 className="text-xl font-bold text-[#213368]">{review.userName}</h2>
                        <p className="text-sm text-gray-500">{review.cycle}</p>
                        <div className="mt-2"><StatusBadge status={review.status} /></div>
                    </div>
                    {isEditable && (
                        <div className="flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-3">
                            <button onClick={handleSendBack} disabled={isSaving} className="px-4 py-2 border border-red-300 text-red-600 rounded hover:bg-red-50 text-sm font-medium flex items-center justify-center"><XCircleIcon className="h-4 w-4 mr-2" /> Send Back</button>
                            <button onClick={handleSaveProgress} disabled={isSaving} className="px-4 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 text-sm font-medium flex items-center justify-center"><SaveIcon className="h-4 w-4 mr-2" /> Save Progress</button>
                            <button onClick={handleCompleteReview} disabled={isSaving} className="px-4 py-2 bg-[#20BEC6] text-white rounded hover:opacity-90 text-sm font-medium flex items-center justify-center shadow-sm"><CheckBadgeIcon className="h-4 w-4 mr-2" /> Complete Review</button>
                        </div>
                    )}
                </div>
                <p className="text-xs text-gray-400 italic">Note: "Complete Review" marks it ready for discussion. You must click the "Submit to HR" icon in the main dashboard to finalize it.</p>
            </div>
            <div className="space-y-8">
                {template.sections.map((section, index) => {
                    const employeeData = review.employeeResponses ? review.employeeResponses[section.id] : null;
                    const comment = managerComments[section.id] || '';
                    return (
                        <div key={section.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                            <h3 className="text-lg font-bold text-[#213368] mb-4 border-b pb-2">{index + 1}. {section.title}</h3>
                            <div className="mb-6">
                                {section.type !== 'rating' && (
                                    <>
                                        <p className="text-xs font-bold text-gray-400 uppercase mb-2">Employee Response</p>
                                        {section.type === 'text' && <div className="bg-gray-50 p-4 rounded text-gray-800 text-sm whitespace-pre-wrap">{employeeData || "No response provided."}</div>}
                                        {(section.type === 'achievements_table' || section.type === 'standard_table' || section.type === 'training_table') && <ReadOnlyTable data={employeeData || []} type={section.type} />}
                                    </>
                                )}
                            </div>
                            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100">
                                <label className="block text-xs font-bold text-yellow-800 uppercase mb-2">Manager Assessment / Comments</label>
                                {section.type === 'rating' ? (
                                    <div>
                                        <select disabled={!isEditable} value={finalRating} onChange={(e) => setFinalRating(e.target.value)} className="w-full p-2 border border-yellow-300 rounded bg-white mb-3 font-medium text-[#213368]">
                                            <option value="">-- Select Final Rating --</option>
                                            {RATING_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                        </select>
                                        <textarea disabled={!isEditable} value={comment} onChange={(e) => handleCommentChange(section.id, e.target.value)} rows={3} className="w-full p-2 border border-yellow-300 rounded bg-white text-sm" placeholder="Overall summary comments (Required)..." />
                                    </div>
                                ) : (
                                    <textarea disabled={!isEditable} value={comment} onChange={(e) => handleCommentChange(section.id, e.target.value)} rows={3} className="w-full p-2 border border-yellow-300 rounded bg-white text-sm" placeholder="Add your feedback on this section..." />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function ReadOnlyTable({ data, type }) {
    if (!data || data.length === 0) return <p className="text-sm text-gray-400 italic">No data entered.</p>;
    return (
        <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                    <tr>
                        {type === 'achievements_table' && <><th className="px-3 py-2 text-left font-medium text-gray-500">Accomplishment</th><th className="px-3 py-2 text-left font-medium text-gray-500">Value</th></>}
                        {type === 'standard_table' && <th className="px-3 py-2 text-left font-medium text-gray-500">Description</th>}
                        {type === 'training_table' && <><th className="px-3 py-2 text-left font-medium text-gray-500">Category</th><th className="px-3 py-2 text-left font-medium text-gray-500">Activity</th><th className="px-3 py-2 text-left font-medium text-gray-500">Status</th></>}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((row, i) => (
                        <tr key={i}>
                            {type === 'achievements_table' && <><td className="px-3 py-2 text-gray-800 whitespace-pre-wrap">{row.text}</td><td className="px-3 py-2 text-gray-600 whitespace-nowrap">{row.value}</td></>}
                            {type === 'standard_table' && <td className="px-3 py-2 text-gray-800 whitespace-pre-wrap">{row.text}</td>}
                            {type === 'training_table' && <><td className="px-3 py-2 text-gray-600">{row.category}</td><td className="px-3 py-2 text-gray-800">{row.name}</td><td className="px-3 py-2 text-gray-600">{row.status}</td></>}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}